/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-prototype-builtins */
import { Grid, Row, Table as AntdTable } from 'antd'
import { TablePaginationConfig } from 'antd/lib/table'
import { SorterResult, TableLocale } from 'antd/lib/table/interface'
import KSearch from 'app/components/Modules/KSearch'
import Empty from 'app/components/UI-Elements/Feedback/Empty'
import PullToRefresh from 'app/components/UI-Elements/General/PullToRefresh'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import { TableProps } from 'app/constants/GlobalTypes'
import { ToolBox } from 'app/lib/hooks/toolBox'
import { useIndex } from 'app/lib/hooks/useApi'
import useFilterParams from 'app/lib/hooks/useFilterParams'
import { ComponentProps, ReactText, useEffect, useState } from 'react'

import { SpinTip } from '../General/KSpin'
import KTableRow from './KTableRow'

interface Props<T> extends TableProps<T & any> {
  recordType: string
  isPullable?: boolean
  searchProps?: ComponentProps<typeof KSearch>
}

function KTable<T>({
  size = 'middle',
  showSearch = false,
  onRowClick,
  onRowHover,
  recordType,
  params,
  locale,
  filters,
  filtersExtra,
  tableExtra,
  empty,
  dataSource,
  renderMobileRow,
  isPullable,
  searchProps,
  loading: loadingProp,
  ...others
}: Props<T>) {
  const {
    params: searchParams,
    searchParams: urlSearchParams,
    setSearchParams
  } = useFilterParams<{ q: string }>()
  const { response, loading, execute } = useIndex<T>(recordType)
  const [searchText, setSearchText] = useState<string>(urlSearchParams.get('query') || '')
  const { md } = Grid.useBreakpoint()
  const { tableRow } = ToolBox.useId()

  useEffect(() => {
    !dataSource && execute({ page: 1, per_page: 10, q: searchText, ...params })
  }, [searchText, JSON.stringify(params)])

  const getRowClassName = (): string => (onRowClick ? 'pointer' : '')

  const handleRefresh = () => {
    return execute({ page: 1, per_page: 10, q: searchText, ...params })
  }

  const getPaginationConfig = (): TablePaginationConfig => {
    return {
      total: response?.pagination?.total_count,
      pageSize: response?.pagination?.per_page,
      hideOnSinglePage: true,
      current: response?.pagination?.current_page,
      showTotal: (total: number, range: [number, number]) =>
        `${range[0]}-${range[1]} von ${total} Ergebnissen`
    }
  }

  const getSortString = (sorter: SorterResult<T>): string => {
    if (
      sorter.hasOwnProperty('order') &&
      (sorter.column?.hasOwnProperty('dataIndex') || sorter.column?.hasOwnProperty('key'))
    ) {
      return `${sorter.order === 'ascend' ? '' : '-'}${
        sorter.column?.key || sorter.column?.dataIndex
      }`
    } else {
      return ''
    }
  }

  const onChange = (
    _p: TablePaginationConfig,
    _f: Record<string, ReactText[]>,
    _sorter: SorterResult<T>
  ) => {
    execute({
      ...params,
      page: _p.current,
      per_page: _p.pageSize,
      q: searchText,
      sort: getSortString(_sorter)
    })
  }

  const onSearch = (value: string) => {
    setSearchText(value)
    setSearchParams({ ...searchParams, query: value })
  }

  const getLocale = (): TableLocale => {
    if (!empty) return locale

    if (searchText && empty.emptySearchText) {
      return {
        ...locale,
        emptyText: <Empty description={empty.emptySearchText} />
      }
    }

    if (empty.default) {
      return {
        ...locale,
        emptyText: empty.default
      }
    }
  }

  const getRow = (props: any) => {
    const { record, ...others } = props
    if (!record) return null
    return <KTableRow renderMobileRow={renderMobileRow} record={record} {...others} />
  }

  const data = dataSource || [...(response?.data ?? [])]

  return (
    <PullToRefresh onRefresh={handleRefresh} isPullable={isPullable}>
      <Row align={'middle'} gutter={[12, { xs: 24, sm: 24, md: 24, lg: 12 }]}>
        <KCol render={Boolean(filters)}>{filters}</KCol>
        <KCol render={showSearch}>
          <KSearch
            defaultValue={searchText}
            onSearch={onSearch}
            style={{ width: 300 }}
            enterButton
            recordType={recordType}
            {...searchProps}
          />
        </KCol>
        <If condition={filtersExtra}>
          <KCol>{filtersExtra}</KCol>
        </If>
        <KCol flex="auto"></KCol>
        <If condition={md}>
          <KCol>{tableExtra}</KCol>
        </If>
        <KCol span={24}>
          <AntdTable
            rowKey={'id'}
            size={size}
            loading={{ spinning: loadingProp || loading, tip: SpinTip }}
            dataSource={data}
            locale={getLocale()}
            onChange={onChange}
            rowClassName={`fade-in-slow ${getRowClassName()}`}
            pagination={getPaginationConfig()}
            components={data.length && renderMobileRow && { body: { row: getRow } }}
            onRow={(record, index) => {
              return {
                id: tableRow({ recordType, id: record?.id }),
                onClick: (e) => onRowClick && onRowClick(record, index, e),
                onMouseEnter: (e) => onRowHover && onRowHover(record, index, e),
                record
              }
            }}
            {...others}
          />
        </KCol>
      </Row>
    </PullToRefresh>
  )
}

export default KTable
